import axios from "axios";
import { useState } from "react";
import Lottie from "lottie-react";
import Thinking from "assets/images/Thinking.json";
import BlueRefreshAnimation from "assets/images/BlueRefreshAnimation.json";
import SuccessModal from "../Communication/SuccessModal";
import TypingInput from "./TypingInput";
import { Img } from "components";
import TextGenrateIconAnimation from "assets/images/TextGenrateIconAnimation.json";
import { API_HOST } from "utils";

const TextArea = ({ isData, setData }) => {
    const [inputValue, setInputValue] = useState("");
    const [category, setCategory] = useState("institute");
    const [isLoading, setIsLoading] = useState(false);
    const [successModal, setSuccessModal] = useState<any>(false);
    const [retryCount, setRetryCount] = useState<number>(0);
    const [zoomed, setZoomed] = useState(false);

    const handleClick = () => {
        setZoomed(true);
        generateText();
    };

    const generateText = async () => {
        try {
            setIsLoading(true);
            const response = await axios.post(`${API_HOST}/master/textGenerate`, {
                category,
                kwargs: { name: inputValue }
            });
            setData(response?.data?.data?.text);
        } catch (err) {
            setSuccessModal(successModal => ({ key: "error", message: err?.message }));
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => setInputValue(e);
    const handleCategoryChange = (e) => {
        setData("");
        setCategory(category => e.target.value);
        setRetryCount(retryCount => 0);
        setInputValue(inputValue => "");
    }

    const handleRegenrate = () => {
        setRetryCount(retryCount => +retryCount + 1);
        generateText();
    }

    const renderUI = () => {
        const commonStyles = "border-[0.3vh] border-smd_golden-light rounded-[1.5vh]";

        if (isLoading) {
            return (
                <div className={`grid place-items-center h-[25vh] text-[2.5vh] ${commonStyles} bg-slate-50 bg-opacity-40`}>
                    <div className="text-center">
                        <Lottie animationData={Thinking} className="h-[10vh] w-auto" />
                        <p className="text-blue-900 mt-[1vh] font-medium">Generating magic...</p>
                    </div>
                </div>
            );
        }

        if (isData) {
            return (
                <div className="">
                    <div className="flex justify-between items-center">
                        <button
                            className="flex justify-center items-center gap-1 text-smd_blue-normal w-fit font-bold"
                            disabled={retryCount >= 5}
                            onClick={handleRegenrate}
                            style={{ opacity: retryCount >= 5 ? 0.5 : 1 }}
                        >
                            <Lottie animationData={BlueRefreshAnimation} className="h-[5vh]" />
                            Regenerate
                        </button>
                        <span className="font-manropeB text-[2.6vh] text-[#787878]"><span className="text-smd_blue-normal">{retryCount}</span>/{5}</span>
                    </div>
                    <div
                        className={`text-[2vh] whitespace-pre-wrap p-[2vh] w-full ${commonStyles} bg-white`}
                        style={{
                            minHeight: "20vh",
                            maxHeight: "20vh",
                            overflowY: "auto"
                        }}
                    >
                        {isData}
                    </div>
                </div>
            );
        }

        return (
            <div
                className={`grid place-items-center text-center text-blue-900 text-[2.2vh] ${commonStyles} bg-slate-50 bg-opacity-40`}
                style={{ height: "25vh" }}
            >
                <div className="font-manropeSB text-[#328E6E]">
                    Your content will be <br /> generated here by our AI
                </div>
            </div>
        );
    };

    return (
        <div className="p-[2vh] rounded-[3vh] space-y-[2vh] bg-[white] shadow-xl shadow-[gray] border border-[gray] h-full">
            {!!successModal?.key && <SuccessModal
                isOpen={successModal}
                onRequestClose={() => {
                    setSuccessModal(false)
                }}
            />}
            <div className="grid gap-[0.5vh] text-center">
                <h2 className="text-[2.8vh] font-bold text-blue-900 mb-2">Instant, AI-Written Content</h2>
                <p className="text-[2vh] text-black font-medium leading-relaxed">
                    Type your school’s name for instant, tailored SEO content.<br />
                    Get unique, engaging text in seconds.
                </p>
            </div>

            <div className="flex justify-between items-center gap-[0.6vh] border-[0.3vh] border-smd_golden-light bg-white rounded-[1.5vh] px-[1vh] py-[0.5vh]">
                <div className="flex items-center gap-1">
                    <select
                        className="text-[2vh] p-[1vh] pr-[4vh] rounded-[1vh] border-none bg-transparent font-medium focus:ring-0 
                    appearance-none [-webkit-appearance:none] [-moz-appearance:none]"
                        onChange={handleCategoryChange}
                    >
                        <option value="institute">School</option>
                        <option value="faculty">Faculty</option>
                    </select>
                    {/* <div className="w-[0.2vh] h-[3vh] bg-gray-300 rounded-[1vh]"></div> */}
                </div>
                <div className="flex-1">
                    {/* <input
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        placeholder={`Your ${category === "institute" ? "school" : "faculty"} name`}
                        className="text-[2vh] p-[1vh] rounded-[1vh] border-none focus:ring-0 placeholder-gray-400"
                    /> */}
                    <TypingInput
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        // fullText={`Your ${category === "institute" ? "school" : "faculty"} name`}
                        fullText={`Enter Your School/Faculty Name`}
                        className="text-[1.2vh] p-[1vh] rounded-[1vh] border-none focus:ring-0 placeholder-gray-400 w-full"
                    />
                </div>
                <div className="flex justify-end">
                    {!isData && <button
                        type="button"
                        onClick={handleClick}
                        disabled={!inputValue}
                        className="bg-smd_blue-normal group p-[0.5vh] rounded-full"
                    >
                        <Lottie animationData={TextGenrateIconAnimation} className={`h-[4.5vh] w-[4.5vh]`}/>
                       {/*  <Img
                            src="/images/soomuch-demo/GenerateTextIcon.svg"
                            alt="generate"
                            className={`h-[4.5vh] w-[4.5vh] transition-transform duration-[500ms] ease-in-out group-hover:rotate-45 ${zoomed ? "scale-125" : ""}`}
                        /> */}
                            
                    </button>}
                </div>
            </div>

            {renderUI()}
        </div>
    );
};

export default TextArea;